import { create } from "zustand";
import { isAiResidentActorId, type BootstrapResponse, type ChatMessage, type ChatRejected, type PropertyPublic, type PublicOfferView, type ServiceDraining } from "@townsy/contracts";
import type { RoomCloseState } from "../net/closeCodes";
import { STORAGE_KEYS, readJson, writeJson } from "../lib/storage";
import type { CheckoutRequest, CheckoutRequestInput } from "../commerce/checkoutRequest";
import { browserLocalStorage, defaultMinimapOpen, readMinimapOpen, writeMinimapOpen } from "../ui/minimap/minimapModel";

/**
 * Application state (docs/02 section 3: the browser only holds input, camera preference, cached
 * public data and short-lived UI state — never ownership, balances or authority).
 */
export type CameraMode = "overview" | "walk";
export type QualityLevel = "low" | "medium" | "high";
export type SceneStatus = "idle" | "loading" | "ready" | "failed" | "fallback";
export type RoomStatus = "idle" | "connecting" | "joined" | "reconnecting" | "transitioning" | "closed";

export interface RoomInfo {
  status: RoomStatus;
  kind: "street" | "interior" | null;
  roomKey: string | null;
  districtId: string | null;
  propertyId: string | null;
  presentationVersion: number | null;
  sessionId: string | null;
  joinedAt: number | null;
  chatDwellSeconds: number;
  chatMinIntervalMs: number;
  /**
   * ADR-0043: the room's people (visitors; AI residents excluded) as the server counts them (room state `peopleCount`).
   * On the street a tab receives only the actors near it, so `actors` is no head count. Null before the first state.
   */
  peopleCount: number | null;
  /**
   * ADR-0043: the world copy (1 = Robin): on the street the room's copy (so `peopleCount` is that copy's people), inside
   * a building the copy the visitor came from. Null before the room says.
   */
  worldCopy: number | null;
}

/**
 * ADR-0043: a switch to another world copy (the Worlds list or "Join them in Finch"): waiting for the server, or refused
 * with the server's sentence (the visitor stays where they are). `copy` is the copy asked for (null for a friend join
 * until the server answers).
 */
export type WorldSwitchUi =
  | { status: "idle" }
  | { status: "pending"; copy: number | null; friendActorId: string | null }
  | { status: "refused"; copy: number | null; code: string; message: string };

export interface RoomProblem {
  state: RoomCloseState;
  title: string;
  message: string;
  retryable: boolean;
  code?: number;
}

/** Who is in the room (keyed by actorId); positions never enter the store. */
export interface ActorSummary {
  actorId: string;
  label: string;
  countryCode: string | null;
  isLocal: boolean;
}

export interface ChatEntry {
  id: string;
  actorId: string;
  label: string;
  countryCode: string | null;
  text: string;
  at: string;
  own: boolean;
  system?: boolean;
}

/** What the report dialog is about: a person, optionally one of their messages (text shown as plain text only). */
export interface PersonReportRequestUi {
  actorId: string;
  label: string;
  conversationId?: string;
  /** The room the reported chat line was said in (taken when the dialog opens; defaults to the current room). */
  roomKey?: string;
  message?: { messageId: string; text: string; at: string };
}

export interface ChatRejection extends ChatRejected {
  at: number;
}

export interface ToastAction {
  label: string;
  run(): void;
}

export interface Toast {
  id: number;
  text: string;
  tone: "info" | "warning" | "danger" | "success";
  /** Buttons on the toast (e.g. "Choose interior" and "View receipt" after buying a store). */
  actions?: ToastAction[];
}

/** The store interior picker opened from anywhere (toast, receipt, checkout, store panel) for one store. */
export interface ThemePickerRequest {
  id: number;
  propertyId: string;
  /** "purchase": opened right after buying the store (toast, receipt, checkout), which the picker mentions. */
  source: "purchase" | "panel";
}

/**
 * "Show on my store" (M5, ADR-0009 #8): the current owner applies an APPROVED storefront creative to a
 * store. Opened from the store panel (store fixed, choose a design) or from the advertiser library
 * (design fixed, choose one of your stores). The server re-checks ownership and approval.
 */
export interface PlacementPickerRequest {
  id: number;
  propertyId: string | null;
  creativeId: string | null;
}

/**
 * "Set up your store" / "Edit your store" (ADR-0020): the one screen for a store's name, logo, website, description,
 * category, the sign inside and the interior. "purchase": opened right after buying it (toast, checkout, receipt).
 */
export interface StoreSetupRequestUi {
  id: number;
  propertyId: string;
  source: "purchase" | "panel" | "directory" | "notice";
  /** ADR-0031: opened from "Re-use your store design": the screen starts filled in from the owner's earlier store. */
  reuse?: boolean;
}

/**
 * "Add an offer" on one screen (ADR-0027): one store's offers at a time (ADR-0029: free, so no budget to focus).
 * focus "reuse" (ADR-0030): opened from "Re-use your offers"; the screen starts filled in from the owner's earlier offers.
 */
export interface OfferSetupRequestUi {
  id: number;
  propertyId: string;
  source: "panel" | "store" | "directory" | "notice" | "checkout" | "advertiser";
  focus: "offers" | "reuse";
}

/**
 * The sponsored-task dialog (M6, ADR-0011): one task at a time, opened from a store's Tasks list or the
 * "Earn coins" list (offer known) or for an attempt in progress (attempt id). The server decides everything.
 */
export interface TaskRequest {
  id: number;
  offer: PublicOfferView | null;
  attemptId: string | null;
  /** Opened from the "Earn coins" list: closing the task returns to the list. */
  fromEarn: boolean;
}

export interface UiState {
  mode: CameraMode;
  quality: QualityLevel;
  sceneStatus: SceneStatus;
  sceneError: string | null;
  selectedPropertyId: string | null;
  statsOpen: boolean;
  directoryOpen: boolean;
  chatOpen: boolean;
  menuOpen: boolean;
  reportOpen: boolean;
  reducedMotion: boolean;
  isTouch: boolean;
  nearbyEntranceId: string | null;
  fps: number;
  transitionPending: boolean;
  returnCooldownUntil: number;
  dismissedNotices: string[];
  toasts: Toast[];
  /** Set when the chat textarea has focus so keyboard movement is suppressed (A09). */
  chatFocused: boolean;
  /** Wallet dialog (Coins + Credit, history). */
  walletOpen: boolean;
  /** Real Estate listings panel: opens automatically inside the office; dismissable and reopenable there. */
  realEstateOpen: boolean;
  /** Townsy Home Builders panel (upgrades, yard items, paint & style): opens automatically inside v-02. */
  homeBuildersOpen: boolean;
  /** Townsy Furnishings panel (furniture, inside style): opens automatically inside v-03. */
  furnishingsOpen: boolean;
  /** "My home" manager (place / remove / rotate items, reset layout, open / closed) for residents with a home. */
  myHomeOpen: boolean;
  myHomeTab: "yard" | "interior";
  /** A stopped, free car whose driver's door the local avatar stands at ("Get in (E)"); street, walk mode only. */
  vehicleCandidateId: string | null;
  /** The car the local player drives (from the authoritative ActorState.vehicleId), or null when walking. */
  drivingVehicleId: string | null;
  /** A get in / get out request is waiting for the server's vehicle.result. */
  vehiclePending: "enter" | "exit" | null;
  /** ADR-0018: the free seat within reach the bottom prompt offers ("Sit on the sofa"); walk mode, on foot, standing. */
  seatOffer: { seatId: string; kind: "sit" | "lie"; place: string } | null;
  /** ADR-0018: the local avatar sits or lies (from the authoritative ActorState.posture), or null when standing. */
  seated: { kind: "sit" | "lie"; place: string | null } | null;
  /** A sit / lie / stand request is waiting for the server's seat.result. */
  seatPending: "sit" | "lie" | "stand" | null;
  /** ADR-0018: the touch Run toggle next to the joystick (desktop holds Shift instead). */
  runToggle: boolean;
  /** The small town map (ADR-0008); the viewer's choice is remembered in localStorage when possible. */
  minimapOpen: boolean;
  /** Street the directory opens filtered to (a street name clicked on the map); null = all streets. */
  directoryStreet: string | null;
  /** "Earn coins" list (sponsored tasks across the town), opened from the HUD coin chip. */
  earnOpen: boolean;
  /** The Tasks panel of the store the visitor is inside (HUD "Tasks" button): shown only while inside that property. */
  storeTasksFor: string | null;
  /** The person whose card is open (clicked in the town; ADR-0013); never the local avatar. */
  selectedActorId: string | null;
  /**
   * The "Report this person" dialog (from the person card, or a private conversation: ADR-0014); `message`: "Report this
   * message" on one chat line or private message, quoted in the dialog and sent as `messageId`.
   */
  personReport: PersonReportRequestUi | null;
  /** Private messages (ADR-0014): the open messages dialog, on a conversation or about to start one with a person. */
  messages: { seq: number; conversationId: string | null; recipientActorId: string | null } | null;
  /** ADR-0043: the Worlds dialog (the open copies, switching, Find a person). */
  worldsOpen: boolean;
  /** ADR-0043: the current switch between world copies, if any. */
  worldSwitch: WorldSwitchUi;
}

const IDLE_ROOM: RoomInfo = {
  status: "idle",
  kind: null,
  roomKey: null,
  districtId: null,
  propertyId: null,
  presentationVersion: null,
  sessionId: null,
  joinedAt: null,
  chatDwellSeconds: 0,
  chatMinIntervalMs: 0,
  peopleCount: null,
  worldCopy: null,
};

const MAX_CHAT_MESSAGES = 200;
let toastSeq = 1;
let checkoutSeq = 1;
let themePickerSeq = 1;
let placementPickerSeq = 1;
let storeSetupSeq = 1;
let offerSetupSeq = 1;
let taskSeq = 1;

/**
 * Overlays that must stop keyboard/joystick movement (A09, docs/01 section 7): statistics,
 * directory, report, wallet, "open in another tab", and the dollar checkout (its Stripe card fields
 * live in an iframe, so the focused element alone is not enough to detect typing).
 */
export function isMovementModalOpen(
  s: Pick<TownStore, "ui" | "roomProblem" | "checkout"> & Partial<Pick<TownStore, "themePicker" | "placementPicker" | "storeSetup" | "offerSetup" | "task">>,
): boolean {
  const ui = s.ui;
  return (
    ui.statsOpen ||
    ui.directoryOpen ||
    ui.reportOpen ||
    ui.walletOpen ||
    Boolean(ui.earnOpen) ||
    Boolean(ui.messages) ||
    Boolean(ui.worldsOpen) ||
    Boolean(ui.personReport) ||
    Boolean(s.task) ||
    s.checkout !== null ||
    Boolean(s.themePicker) ||
    Boolean(s.placementPicker) ||
    Boolean(s.storeSetup) ||
    Boolean(s.offerSetup) ||
    s.roomProblem?.state === "control_taken"
  );
}

/** A PROPERTY_RESERVED answer is shown on the property panel for this long. */
export const RESERVED_NOTICE_MS = 10 * 60_000;

/**
 * Founder 2026-10-26: the town always opens in Overview (the point is advertising: the building logos seen from above);
 * Walk is a choice made after loading. The mode is no longer remembered between loads.
 */
function initialMode(): CameraMode {
  return "overview";
}

function initialMinimapOpen(): boolean {
  return readMinimapOpen(browserLocalStorage, defaultMinimapOpen(typeof window === "undefined" ? null : window.innerWidth));
}

function initialQuality(): QualityLevel {
  const saved = readJson<QualityLevel>("local", STORAGE_KEYS.quality);
  return saved === "low" || saved === "high" ? saved : "medium";
}

export interface TownStore {
  bootstrap: BootstrapResponse | null;
  properties: Record<string, PropertyPublic>;
  propertyIds: string[];
  worldVersion: number | null;
  room: RoomInfo;
  roomProblem: RoomProblem | null;
  draining: ServiceDraining | null;
  degraded: string | null;
  actors: Record<string, ActorSummary>;
  /**
   * ADR-0043: the open person card's person after they left this tab's view on the street (walked out of range, went
   * inside or left: the tab cannot tell which). The card stays open, marked "not nearby"; null otherwise.
   */
  selectedAway: ActorSummary | null;
  localActorId: string | null;
  chat: { messages: ChatEntry[]; rejection: ChatRejection | null; unread: number; lastSentAt: number };
  ui: UiState;
  /** The open dollar checkout (one at a time); blocks avatar movement while set (A09). */
  checkout: CheckoutRequest | null;
  /** Orders still settling after their checkout dialog closed (polled in the background, toasted once). */
  watchedOrders: string[];
  /** Properties for which the server recently answered PROPERTY_RESERVED (epoch ms), for the panel notice. */
  reservedSeen: Record<string, number>;
  /** The open store interior picker (one at a time); the server re-checks ownership on save. */
  themePicker: ThemePickerRequest | null;
  /** The open "Show on my store" picker (one at a time). */
  placementPicker: PlacementPickerRequest | null;
  /** The open "Set up your store" screen (one at a time; ADR-0020); blocks avatar movement while set. */
  storeSetup: StoreSetupRequestUi | null;
  /** The open "Add an offer" screen (one at a time; ADR-0027); blocks avatar movement while set. */
  offerSetup: OfferSetupRequestUi | null;
  /** The open sponsored-task dialog (one at a time); blocks avatar movement while set (A09). */
  task: TaskRequest | null;
  /**
   * The district the visitor is in (ADR-0041; town v6, ADR-0042: on the street the district of the avatar's position,
   * updated as they walk or drive; inside, the building's). Set by the town session; read by the HUD, the map and the
   * directory. Default: the world's first district.
   */
  districtId: string;
  setDistrictId(districtId: string): void;
  /**
   * People this visitor muted (ADR-0013): actorId -> the account's block id (server), or null for a guest's mute kept in
   * this browser. Their chat lines and speech bubbles are hidden here.
   */
  mutes: Record<string, string | null>;
  /** Text the chat box takes once ("Say hi" from the person card: "@Name "). */
  chatDraft: { seq: number; text: string } | null;
  /** The latest private message notice from the realtime room (ADR-0014): readers re-read the API. */
  dmPing: { seq: number; conversationId: string } | null;

  openCheckout(req: CheckoutRequestInput): void;
  closeCheckout(): void;
  watchOrder(orderId: string): void;
  unwatchOrder(orderId: string): void;
  markReserved(propertyId: string, at?: number): void;
  openThemePicker(propertyId: string, source?: ThemePickerRequest["source"]): void;
  closeThemePicker(): void;
  openPlacementPicker(req: { propertyId?: string | null; creativeId?: string | null }): void;
  closePlacementPicker(): void;
  openStoreSetup(propertyId: string, source?: StoreSetupRequestUi["source"], opts?: { reuse?: boolean }): void;
  closeStoreSetup(): void;
  openOfferSetup(propertyId: string, source?: OfferSetupRequestUi["source"], focus?: OfferSetupRequestUi["focus"]): void;
  closeOfferSetup(): void;
  openTask(req: { offer?: PublicOfferView | null; attemptId?: string | null }): void;
  closeTask(): void;
  selectActor(actorId: string | null): void;
  openPersonReport(req: PersonReportRequestUi | null): void;
  openMessages(req?: { conversationId?: string | null; recipientActorId?: string | null } | null): void;
  closeMessages(): void;
  pingDm(conversationId: string): void;
  setMutes(mutes: Record<string, string | null>): void;
  setMuted(actorId: string, muted: boolean, blockId?: string | null): void;
  setChatDraft(text: string | null): void;
  setMinimapOpen(open: boolean): void;
  /** Open the directory, optionally filtered to one street. */
  openDirectory(street?: string | null): void;

  setBootstrap(b: BootstrapResponse | null): void;
  setProperties(items: PropertyPublic[], worldVersion: number): void;
  upsertProperty(item: PropertyPublic): void;
  setRoom(patch: Partial<RoomInfo>): void;
  resetRoom(): void;
  setRoomProblem(p: RoomProblem | null): void;
  setDraining(d: ServiceDraining | null): void;
  setDegraded(message: string | null): void;
  replaceActors(list: ActorSummary[]): void;
  upsertActorSummary(a: ActorSummary): void;
  removeActorSummary(actorId: string): void;
  setLocalActorId(id: string | null): void;
  addChatMessage(m: ChatMessage, own: boolean): void;
  addSystemChat(text: string): void;
  setChatRejection(r: ChatRejected | null): void;
  removeChatMessage(id: string): void;
  clearChat(): void;
  markChatSent(at: number): void;
  setUi(patch: Partial<UiState>): void;
  setMode(mode: CameraMode): void;
  setQuality(q: QualityLevel): void;
  selectProperty(id: string | null): void;
  toggleStats(open?: boolean): void;
  toggleDirectory(open?: boolean): void;
  toggleChat(open?: boolean): void;
  pushToast(text: string, tone?: Toast["tone"], action?: ToastAction | ToastAction[]): void;
  dismissToast(id: number): void;
  dismissNotice(code: string): void;
}

export const useTownStore = create<TownStore>()((set, get) => ({
  bootstrap: null,
  properties: {},
  propertyIds: [],
  worldVersion: null,
  room: IDLE_ROOM,
  roomProblem: null,
  draining: null,
  degraded: null,
  actors: {},
  selectedAway: null,
  localActorId: null,
  chat: { messages: [], rejection: null, unread: 0, lastSentAt: 0 },
  ui: {
    mode: initialMode(),
    quality: initialQuality(),
    sceneStatus: "idle",
    sceneError: null,
    selectedPropertyId: null,
    statsOpen: false,
    directoryOpen: false,
    chatOpen: false,
    menuOpen: false,
    reportOpen: false,
    reducedMotion: false,
    isTouch: false,
    nearbyEntranceId: null,
    fps: 0,
    transitionPending: false,
    returnCooldownUntil: 0,
    dismissedNotices: readJson<string[]>("session", STORAGE_KEYS.dismissedNotices) ?? [],
    toasts: [],
    chatFocused: false,
    walletOpen: false,
    realEstateOpen: false,
    homeBuildersOpen: false,
    furnishingsOpen: false,
    myHomeOpen: false,
    myHomeTab: "yard",
    vehicleCandidateId: null,
    drivingVehicleId: null,
    vehiclePending: null,
    seatOffer: null,
    seated: null,
    seatPending: null,
    runToggle: false,
    minimapOpen: initialMinimapOpen(),
    directoryStreet: null,
    earnOpen: false,
    storeTasksFor: null,
    selectedActorId: null,
    personReport: null,
    messages: null,
    worldsOpen: false,
    worldSwitch: { status: "idle" },
  },
  checkout: null,
  watchedOrders: [],
  reservedSeen: {},
  themePicker: null,
  placementPicker: null,
  storeSetup: null,
  offerSetup: null,
  task: null,
  districtId: "central",
  mutes: {},
  chatDraft: null,
  dmPing: null,

  openCheckout: (req) => set({ checkout: { ...req, id: checkoutSeq++ } as CheckoutRequest }),
  closeCheckout: () => set({ checkout: null }),
  watchOrder: (orderId) => set((s) => (s.watchedOrders.includes(orderId) ? {} : { watchedOrders: [...s.watchedOrders, orderId].slice(-5) })),
  unwatchOrder: (orderId) => set((s) => (s.watchedOrders.includes(orderId) ? { watchedOrders: s.watchedOrders.filter((x) => x !== orderId) } : {})),
  markReserved: (propertyId, at = Date.now()) => set((s) => ({ reservedSeen: { ...s.reservedSeen, [propertyId]: at } })),
  openThemePicker: (propertyId, source = "purchase") => set({ themePicker: { id: themePickerSeq++, propertyId, source } }),
  closeThemePicker: () => set({ themePicker: null }),
  openPlacementPicker: (req) => set({ placementPicker: { id: placementPickerSeq++, propertyId: req.propertyId ?? null, creativeId: req.creativeId ?? null } }),
  closePlacementPicker: () => set({ placementPicker: null }),
  openStoreSetup: (propertyId, source = "panel", opts = {}) => set({ storeSetup: { id: storeSetupSeq++, propertyId, source, ...(opts.reuse ? { reuse: true } : {}) } }),
  closeStoreSetup: () => set({ storeSetup: null }),
  // One screen at a time: opening the offers screen closes the store screen (and the reverse is done by its caller).
  openOfferSetup: (propertyId, source = "panel", focus = "offers") => set({ offerSetup: { id: offerSetupSeq++, propertyId, source, focus }, storeSetup: null }),
  closeOfferSetup: () => set({ offerSetup: null }),
  // The task dialog replaces the Earn coins list on screen (one modal at a time); the list reopens on demand.
  openTask: (req) =>
    set((s) => ({ task: { id: taskSeq++, offer: req.offer ?? null, attemptId: req.attemptId ?? null, fromEarn: s.ui.earnOpen }, ui: { ...s.ui, earnOpen: false } })),
  closeTask: () => set((s) => ({ task: null, ui: s.task?.fromEarn ? { ...s.ui, earnOpen: true } : s.ui })),
  selectActor: (actorId) =>
    set((s) => {
      const id = actorId && actorId !== s.localActorId && s.actors[actorId] && !s.actors[actorId].isLocal ? actorId : null;
      // Choosing someone else (or closing the card) forgets the person who went out of view.
      const selectedAway = s.selectedAway && s.selectedAway.actorId === id ? s.selectedAway : null;
      return s.ui.selectedActorId === id && s.selectedAway === selectedAway ? {} : { selectedAway, ui: { ...s.ui, selectedActorId: id } };
    }),
  openPersonReport: (req) => set((s) => ({ ui: { ...s.ui, personReport: req } })),
  openMessages: (req) =>
    set((s) => ({ ui: { ...s.ui, menuOpen: false, messages: { seq: (s.ui.messages?.seq ?? 0) + 1, conversationId: req?.conversationId ?? null, recipientActorId: req?.recipientActorId ?? null } } })),
  closeMessages: () => set((s) => ({ ui: { ...s.ui, messages: null } })),
  pingDm: (conversationId) => set((s) => ({ dmPing: { seq: (s.dmPing?.seq ?? 0) + 1, conversationId } })),
  setMutes: (mutes) => set({ mutes }),
  setMuted: (actorId, muted, blockId = null) =>
    set((s) => {
      const mutes = { ...s.mutes };
      if (muted) mutes[actorId] = blockId;
      else delete mutes[actorId];
      return { mutes };
    }),
  setChatDraft: (text) => set((s) => ({ chatDraft: text === null ? null : { seq: (s.chatDraft?.seq ?? 0) + 1, text } })),
  setMinimapOpen: (open) => {
    writeMinimapOpen(browserLocalStorage, open);
    set((s) => ({ ui: { ...s.ui, minimapOpen: open } }));
  },
  openDirectory: (street = null) => set((s) => ({ ui: { ...s.ui, directoryOpen: true, directoryStreet: street } })),

  setBootstrap: (bootstrap) => set({ bootstrap }),

  setProperties: (items, worldVersion) =>
    set((s) => {
      const properties: Record<string, PropertyPublic> = {};
      for (const item of items) {
        const existing = s.properties[item.id];
        // Never roll back to an older version (docs/06 section 8).
        properties[item.id] = existing && existing.version > item.version ? existing : item;
      }
      return { properties, propertyIds: items.map((i) => i.id), worldVersion };
    }),

  upsertProperty: (item) =>
    set((s) => {
      const existing = s.properties[item.id];
      if (existing && existing.version > item.version) return {};
      const propertyIds = s.propertyIds.includes(item.id) ? s.propertyIds : [...s.propertyIds, item.id];
      return { properties: { ...s.properties, [item.id]: item }, propertyIds };
    }),

  setRoom: (patch) => set((s) => ({ room: { ...s.room, ...patch } })),
  resetRoom: () => set((s) => ({ room: IDLE_ROOM, actors: {}, selectedAway: null, localActorId: null, ui: { ...s.ui, selectedActorId: null } })),
  setRoomProblem: (roomProblem) => set({ roomProblem }),
  setDraining: (draining) => set({ draining }),
  setDegraded: (degraded) => set({ degraded }),

  replaceActors: (list) =>
    set((s) => {
      const actors = Object.fromEntries(list.map((a) => [a.actorId, a]));
      const id = s.ui.selectedActorId;
      if (!id) return { actors };
      if (actors[id]) return { actors, selectedAway: null };
      // The open card's person is not in the new list: on the street keep the card (out of view, ADR-0043), else close it.
      const last = s.actors[id] ?? (s.selectedAway?.actorId === id ? s.selectedAway : null);
      return s.room.kind === "street" && last ? { actors, selectedAway: last } : { actors, selectedAway: null, ui: { ...s.ui, selectedActorId: null } };
    }),
  upsertActorSummary: (a) =>
    set((s) => {
      const existing = s.actors[a.actorId];
      const back = s.selectedAway?.actorId === a.actorId;
      if (existing && existing.label === a.label && existing.countryCode === a.countryCode && existing.isLocal === a.isLocal && !back) return {};
      return back ? { actors: { ...s.actors, [a.actorId]: a }, selectedAway: null } : { actors: { ...s.actors, [a.actorId]: a } };
    }),
  removeActorSummary: (actorId) =>
    set((s) => {
      if (!(actorId in s.actors)) return {};
      const actors = { ...s.actors };
      const last = actors[actorId]!;
      delete actors[actorId];
      if (s.ui.selectedActorId !== actorId) return { actors };
      // ADR-0043: on the street a person also leaves a tab's state by walking out of its view (the tab cannot tell that
      // from leaving), so their card stays open, marked "not nearby"; its actions all work by actor id. Inside a building
      // (everyone sees everyone) leaving the state means leaving the room: the card closes, as before.
      return s.room.kind === "street" ? { actors, selectedAway: last } : { actors, ui: { ...s.ui, selectedActorId: null } };
    }),
  setLocalActorId: (localActorId) => set({ localActorId }),

  addChatMessage: (m, own) =>
    set((s) => {
      if (s.chat.messages.some((x) => x.id === m.id)) return {};
      const entry: ChatEntry = { id: m.id, actorId: m.actorId, label: m.label, countryCode: m.countryCode, text: m.text, at: m.at, own };
      const messages = [...s.chat.messages, entry].slice(-MAX_CHAT_MESSAGES);
      const unread = s.ui.chatOpen || own || m.actorId in s.mutes ? s.chat.unread : s.chat.unread + 1;
      return { chat: { ...s.chat, messages, unread } };
    }),
  addSystemChat: (text) =>
    set((s) => {
      const entry: ChatEntry = {
        id: `sys_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        actorId: "",
        label: "Townsy",
        countryCode: null,
        text,
        at: new Date().toISOString(),
        own: false,
        system: true,
      };
      return { chat: { ...s.chat, messages: [...s.chat.messages, entry].slice(-MAX_CHAT_MESSAGES) } };
    }),
  setChatRejection: (r) => set((s) => ({ chat: { ...s.chat, rejection: r ? { ...r, at: Date.now() } : null } })),
  removeChatMessage: (id) => set((s) => ({ chat: { ...s.chat, messages: s.chat.messages.filter((m) => m.id !== id) } })),
  clearChat: () => set((s) => ({ chat: { ...s.chat, messages: [], rejection: null, unread: 0 } })),
  markChatSent: (at) => set((s) => ({ chat: { ...s.chat, lastSentAt: at, rejection: null } })),

  setUi: (patch) => set((s) => ({ ui: { ...s.ui, ...patch } })),
  setDistrictId: (districtId) => set((s) => (s.districtId === districtId ? s : { districtId })),
  setMode: (mode) => set((s) => ({ ui: { ...s.ui, mode } })),
  setQuality: (quality) => {
    writeJson("local", STORAGE_KEYS.quality, quality);
    set((s) => ({ ui: { ...s.ui, quality } }));
  },
  selectProperty: (id) => set((s) => ({ ui: { ...s.ui, selectedPropertyId: id, reportOpen: id ? s.ui.reportOpen : false } })),
  toggleStats: (open) => set((s) => ({ ui: { ...s.ui, statsOpen: open ?? !s.ui.statsOpen } })),
  toggleDirectory: (open) =>
    set((s) => {
      const directoryOpen = open ?? !s.ui.directoryOpen;
      // The street preset (from the map) applies to one opening only.
      return { ui: { ...s.ui, directoryOpen, directoryStreet: directoryOpen ? s.ui.directoryStreet : null } };
    }),
  toggleChat: (open) =>
    set((s) => {
      const chatOpen = open ?? !s.ui.chatOpen;
      return { ui: { ...s.ui, chatOpen, chatFocused: chatOpen ? s.ui.chatFocused : false }, chat: { ...s.chat, unread: chatOpen ? 0 : s.chat.unread } };
    }),
  pushToast: (text, tone = "info", action) => {
    const id = toastSeq++;
    const actions = action ? (Array.isArray(action) ? action : [action]) : [];
    const toast: Toast = actions.length > 0 ? { id, text, tone, actions } : { id, text, tone };
    set((s) => ({ ui: { ...s.ui, toasts: [...s.ui.toasts, toast].slice(-4) } }));
    // Toasts with an action stay long enough to be used (keyboard and screen-reader users too).
    setTimeout(() => get().dismissToast(id), actions.length > 0 ? 15000 : tone === "danger" ? 9000 : 5000);
  },
  dismissToast: (id) => set((s) => ({ ui: { ...s.ui, toasts: s.ui.toasts.filter((t) => t.id !== id) } })),
  dismissNotice: (code) =>
    set((s) => {
      const dismissedNotices = [...new Set([...s.ui.dismissedNotices, code])];
      writeJson("session", STORAGE_KEYS.dismissedNotices, dismissedNotices);
      return { ui: { ...s.ui, dismissedNotices } };
    }),
}));

export const selectProperty = (id: string | null) => (s: TownStore) => (id ? (s.properties[id] ?? null) : null);
export const selectIsGuest = (s: TownStore) => !s.bootstrap?.session.userId;
/**
 * People in this room right now, as the SERVER counts them (ADR-0043: room state `peopleCount`; AI residents are never
 * counted, ADR-0010). Not the actors this tab receives: on the street those are only the ones near it. Null until the
 * room's first state.
 */
export const selectHereNow = (s: TownStore): number | null => s.room.peopleCount;
/** People among the actors this tab has in view (diagnostics; never a head count on the street, see selectHereNow). */
export const selectPeopleInView = (s: TownStore) => Object.keys(s.actors).filter((id) => !isAiResidentActorId(s.actors[id]?.actorId ?? id)).length;
/**
 * The person whose card is open: in this tab's view, or (street, ADR-0043) last seen before they went out of it
 * (`away: true`). Null when no card is open.
 */
export const selectCardPerson = (s: TownStore): { actor: ActorSummary; away: boolean } | null => {
  const id = s.ui.selectedActorId;
  if (!id) return null;
  const here = s.actors[id];
  if (here) return { actor: here, away: false };
  return s.selectedAway && s.selectedAway.actorId === id ? { actor: s.selectedAway, away: true } : null;
};
